import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { updateMetaTags } from '../utils/seo'

const MacBookStartetNichtPage: React.FC = () => {
  const navigate = useNavigate()
  const go = (path: string) => { navigate(path); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50) }

  useEffect(() => {
    updateMetaTags({
      title: 'MacBook startet nicht – Ursachen & Reparatur ab 119€ | PhoneMedic',
      description: 'MacBook startet nicht? ✓ Diagnose 24,95€ ✓ Platinen-Reparatur ab 119€ ✓ Kurzschluss, PMIC, T2-Chip ✓ 12 Monate Garantie ✓ Bundesweiter Einsende-Service | PhoneMedic',
      keywords: ['MacBook startet nicht', 'MacBook bootet nicht', 'MacBook kein Lebenszeichen', 'MacBook Platinen Reparatur', 'MacBook Logicboard', 'MacBook schaltet nicht ein'],
      canonicalUrl: 'https://www.phone-medic.de/macbook-startet-nicht',
    })

    const schema = [
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: 'MacBook startet nicht – Diagnose & Reparatur',
        description: 'Professionelle Diagnose und Chip-Level Reparatur wenn das MacBook nicht startet. Kurzschluss, PMIC, T2-Chip, Wasserschaden – wir finden die Ursache.',
        provider: { '@type': 'LocalBusiness', name: 'PhoneMedic', url: 'https://www.phone-medic.de', telephone: '+491605010907' },
        offers: { '@type': 'Offer', price: '119.00', priceCurrency: 'EUR', priceValidUntil: '2026-12-31' },
        areaServed: 'DE',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Warum startet mein MacBook nicht mehr?',
            acceptedAnswer: { '@type': 'Answer', text: 'Die häufigsten Ursachen: defekter PMIC (Power Management IC), Kurzschluss durch Oxidation, beschädigter T2/T1-Sicherheitschip, defekter USB-C-Ladeport oder oxidierte Kondensatoren nach einem Wasserschaden. Eine Diagnose unter dem Mikroskop zeigt die genaue Ursache.' },
          },
          {
            '@type': 'Question',
            name: 'Was kostet die Reparatur wenn das MacBook nicht startet?',
            acceptedAnswer: { '@type': 'Answer', text: 'Die Diagnose kostet 24,95 €. Danach erhältst du einen verbindlichen Kostenvoranschlag. Platinen-Reparaturen beginnen ab 119 €. Du entscheidest, ob du die Reparatur durchführen lässt.' },
          },
          {
            '@type': 'Question',
            name: 'Kann Apple ein MacBook reparieren das nicht startet?',
            acceptedAnswer: { '@type': 'Answer', text: 'Apple repariert keine Platinen auf Chip-Ebene. Bei nicht startenden MacBooks empfiehlt Apple in der Regel ein neues Logicboard für 800–1.500 € oder einen Neukauf. PhoneMedic repariert gezielt den defekten Chip oder die beschädigte Leiterbahn – oft ab 119 €.' },
          },
          {
            '@type': 'Question',
            name: 'Wie lange dauert die Reparatur?',
            acceptedAnswer: { '@type': 'Answer', text: 'Nach Eingang deines MacBooks dauert die Diagnose 1–2 Werktage. Die eigentliche Reparatur dauert dann 2–4 weitere Werktage, je nach Umfang. Express-Service auf Anfrage möglich.' },
          },
          {
            '@type': 'Question',
            name: 'Gehen bei der Reparatur meine Daten verloren?',
            acceptedAnswer: { '@type': 'Answer', text: 'Bei Platinen-Reparaturen bleibt der Speicherchip in der Regel unberührt. Wir informieren dich vorher, falls der Speicher betroffen sein könnte. Eine Datensicherung vor dem Einsenden empfehlen wir trotzdem grundsätzlich.' },
          },
          {
            '@type': 'Question',
            name: 'Was soll ich tun bevor ich mein MacBook einsende?',
            acceptedAnswer: { '@type': 'Answer', text: 'Falls das MacBook noch teilweise funktioniert, erstelle zuerst ein Backup. Dann einfach per E-Mail oder WhatsApp melden – wir schicken dir ein kostenloses DHL-Versandlabel.' },
          },
        ],
      },
    ]

    let script = document.querySelector('#macbook-startet-nicht-schema') as HTMLScriptElement
    if (!script) {
      script = document.createElement('script')
      script.id = 'macbook-startet-nicht-schema'
      script.type = 'application/ld+json'
      document.head.appendChild(script)
    }
    script.textContent = JSON.stringify(schema)
    return () => { document.querySelector('#macbook-startet-nicht-schema')?.remove() }
  }, [])

  const causes = [
    { icon: '⚡', title: 'Kurzschluss auf der Platine', desc: 'Ein Kurzschluss verhindert das Hochfahren. Oft verursacht durch Flüssigkeiten, Oxidation oder mechanischen Druck. Unter dem Mikroskop genau lokalisierbar und reparierbar.' },
    { icon: '🔋', title: 'Defekter PMIC', desc: 'Der Power Management IC steuert alle Spannungen auf der Platine. Fällt er aus, bekommt das MacBook keinen Strom – auch wenn der Akku voll ist.' },
    { icon: '🔒', title: 'T2 / T1 Sicherheitschip', desc: 'Apples Sicherheitschip kann bei Defekt den gesamten Startvorgang blockieren. Ein häufig übersehener Grund warum nichts mehr funktioniert.' },
    { icon: '🔌', title: 'Defekter USB-C Ladeport', desc: 'Ein beschädigter Ladeport überträgt keinen Strom zur Platine. Das MacBook zeigt kein Lebenszeichen – obwohl der Rest intakt ist.' },
    { icon: '💧', title: 'Wasserschaden / Korrosion', desc: 'Feuchtigkeit hinterlässt Korrosion, die Leiterbahnen unterbricht. Auch wenn der Schaden Monate zurückliegt, kann er die Ursache sein.' },
    { icon: '🧩', title: 'Defekte Kondensatoren / ICs', desc: 'Einzelne SMD-Bauteile altern oder versagen – besonders bei älteren Intel MacBooks. Ein defekter Chip stoppt den gesamten Startvorgang.' },
  ]

  const faqs = [
    { q: 'Warum startet mein MacBook nicht mehr?', a: 'Die häufigsten Ursachen sind defekter PMIC, Kurzschluss durch Oxidation, beschädigter T2-Chip, defekter USB-C-Ladeport oder Wasserschaden. Eine Diagnose unter dem Mikroskop zeigt die genaue Ursache.' },
    { q: 'Was kostet die Reparatur wenn das MacBook nicht startet?', a: 'Diagnose: 24,95 €. Platinen-Reparatur ab 119 €. Du bekommst zuerst einen verbindlichen Kostenvoranschlag und entscheidest dann.' },
    { q: 'Kann Apple ein MacBook reparieren das nicht startet?', a: 'Apple repariert keine Chips auf Platinen-Ebene. Apple empfiehlt ein neues Logicboard für 800–1.500 € oder einen Neukauf. Wir reparieren den defekten Chip gezielt – oft ab 119 €.' },
    { q: 'Wie lange dauert die Reparatur?', a: 'Diagnose: 1–2 Werktage. Reparatur: 2–4 Werktage. Express-Service auf Anfrage.' },
    { q: 'Gehen meine Daten verloren?', a: 'Bei Platinen-Reparaturen bleibt der Speicherchip in der Regel unberührt. Wir informieren dich vorher falls der Speicher betroffen sein könnte.' },
    { q: 'Lohnt sich eine Reparatur wenn das MacBook nicht mehr startet?', a: 'In 70–90 % der Fälle ja. Die Ursache ist oft ein einzelner defekter Chip, kein kompletter Platinentausch. Eine Diagnose zeigt, ob eine Reparatur sinnvoll ist.' },
  ]

  return (
    <div className="min-h-screen bg-white">
      <section
        className="relative text-white py-20"
        style={{ backgroundImage: 'url(/hero-bg.webp)', backgroundSize: 'cover', backgroundPosition: 'center top' }}
      >
        <div className="absolute inset-0 bg-gray-900/80" />
        <div className="relative max-w-7xl mx-auto px-4">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-indigo-600/20 border border-indigo-500/30 text-indigo-300 text-sm font-medium px-3 py-1 rounded-full mb-6">
              <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full"></span>
              Platinen-Reparatur · Mikrochip-Löten
            </div>
            <h1 className="text-5xl font-bold mb-6 leading-tight">
              MacBook startet nicht –<br />
              <span className="text-indigo-400">Ursachen & Reparatur</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl">
              Kein Lebenszeichen, schwarzer Bildschirm, schaltet sofort wieder aus? Wir diagnostizieren und reparieren auf Chip-Ebene – auch wenn Apple aufgibt. Bundesweiter Einsende-Service.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="mailto:service@phone-medic.de" className="bg-indigo-600 text-white px-7 py-3.5 rounded-lg font-semibold hover:bg-indigo-700 transition-colors">
                Kostenvoranschlag anfragen
              </a>
              <button onClick={() => go('/macbook-reparatur')} className="bg-white/10 text-white border border-white/20 px-7 py-3.5 rounded-lg font-semibold hover:bg-white/20 transition-colors">
                Alle MacBook Services
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gray-800 text-white py-5 border-t border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            {[
              { number: '24,95 €', label: 'Diagnose' },
              { number: 'ab 119 €', label: 'Platinen-Reparatur' },
              { number: '70–90 %', label: 'Erfolgsquote' },
              { number: '12 Monate', label: 'Garantie' },
            ].map((s, i) => (
              <div key={i}>
                <div className="text-2xl font-bold text-indigo-400">{s.number}</div>
                <div className="text-sm text-gray-400">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Warum startet das MacBook nicht?</h2>
            <p className="text-gray-500 max-w-xl mx-auto">Die häufigsten Ursachen – und warum sie reparierbar sind</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {causes.map((c, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="font-bold text-gray-900 mb-2">{c.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Chip-Level Reparatur statt Neukauf</h2>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Apple repariert keine Platinen auf Chip-Ebene. Der Standardsatz lautet: „Das Logicboard muss getauscht werden" – Kosten: 800–1.500 €. Oft ist das der Startschuss für einen 1.500 € Neukauf.
              </p>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Wir arbeiten anders. Mit Mikroskop und Mikrolöt-Station identifizieren wir den defekten Chip oder die unterbrochene Leiterbahn – und reparieren genau diesen Punkt. Das MacBook bleibt, die Daten bleiben, und der Preis liegt ab 119 €.
              </p>
              <div className="bg-gray-50 rounded-xl border border-gray-200 p-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-bold text-gray-500 mb-3 text-sm uppercase tracking-wide">Apple / normale Werkstatt</h4>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex gap-2"><span className="text-red-400 shrink-0">✗</span>Kein Chip-Level Service</li>
                      <li className="flex gap-2"><span className="text-red-400 shrink-0">✗</span>Logicboard tauschen: 800–1.500 €</li>
                      <li className="flex gap-2"><span className="text-red-400 shrink-0">✗</span>Daten gehen verloren</li>
                      <li className="flex gap-2"><span className="text-red-400 shrink-0">✗</span>Empfehlung: Neukauf</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3 text-sm uppercase tracking-wide">PhoneMedic</h4>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex gap-2"><span className="text-green-600 shrink-0">✓</span>Diagnose unter dem Mikroskop</li>
                      <li className="flex gap-2"><span className="text-green-600 shrink-0">✓</span>Chip-Reparatur ab 119 €</li>
                      <li className="flex gap-2"><span className="text-green-600 shrink-0">✓</span>Daten bleiben erhalten</li>
                      <li className="flex gap-2"><span className="text-green-600 shrink-0">✓</span>12 Monate Garantie</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-6">
                <h3 className="font-bold text-indigo-900 mb-2">Ablauf: MacBook einsenden</h3>
                <ol className="space-y-3">
                  {[
                    'Kontakt per E-Mail oder WhatsApp – wir schicken dir ein kostenloses DHL-Label',
                    'MacBook sicher verpacken und einsenden',
                    'Diagnose unter dem Mikroskop (1–2 Werktage) – du erhältst einen Kostenvoranschlag',
                    'Reparatur nach deiner Freigabe (2–4 Werktage)',
                    'MacBook versichert zurück zu dir',
                  ].map((step, i) => (
                    <li key={i} className="flex gap-3 text-sm text-indigo-800">
                      <span className="w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">{i + 1}</span>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-amber-800 text-sm font-medium">
                  <strong>Tipp:</strong> Falls dein MacBook noch teilweise startet, erstelle zuerst ein Backup – auch wenn der Bildschirm schwarz bleibt, kann oft per USB auf die Daten zugegriffen werden.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-10">Häufige Fragen</h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">{faq.q}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Weitere MacBook Services</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'MacBook Wasserschaden', path: '/macbook-wasserschaden' },
              { label: 'MacBook Akku tauschen', path: '/macbook-akku-tauschen' },
              { label: 'MacBook Display Reparatur', path: '/macbook-display-reparatur' },
              { label: 'Logicboard Reparatur', path: '/macbook-logicboard-reparatur' },
            ].map((item) => (
              <button key={item.path} onClick={() => go(item.path)} className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm font-medium text-gray-700 hover:border-indigo-400 hover:text-indigo-700 hover:bg-indigo-50 transition-colors text-center">
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-3">MacBook startet nicht? Wir schauen es uns an.</h2>
          <p className="text-gray-400 mb-8">Diagnose 24,95 € · Kostenvoranschlag vor Reparaturbeginn · 12 Monate Garantie</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="mailto:service@phone-medic.de" className="bg-indigo-600 text-white px-7 py-3.5 rounded-lg font-semibold hover:bg-indigo-700 transition-colors">
              Per E-Mail anfragen
            </a>
            <a href="https://wa.me/491605010907" target="_blank" rel="noopener noreferrer" className="bg-white text-gray-900 px-7 py-3.5 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
              WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}

export default MacBookStartetNichtPage
